import React, { type ReactElement, useMemo } from 'react';
import RangeSlider, {
  useRangeQueryParam
} from 'feature-components/reports/item-assessment-report/controls/range-slider';
import SortSelector, {
  type Sort,
  useSortQueryParams
} from 'feature-components/reports/item-assessment-report/controls/select-sort-type';

const defaultEncounterThreshold = 1;
const defaultPercentCorrect = 50;
const defaultItemSize = 6;

export function useItemAssessmentReportSettings(): {
  minEncounters: number;
  minPercentageCorrect: number;
  iconWidth: number;
  itemSorting: Sort;
} {
  const [minEncounters] = useRangeQueryParam('encounters', defaultEncounterThreshold);
  const [minPercentageCorrect] = useRangeQueryParam('percent-correct', defaultPercentCorrect);
  const [iconWidth] = useRangeQueryParam('icon-size', defaultItemSize);
  const itemSorting = useSortQueryParams();

  return useMemo(
    () => ({ minEncounters, minPercentageCorrect, iconWidth, itemSorting }),
    [iconWidth, minEncounters, itemSorting, minPercentageCorrect]
  );
}

type Props = { maxEncounters: number };
export default function ItemAssessmentReportControls(props: Props): ReactElement {
  const { maxEncounters } = props;

  return (
    <>
      <RangeSlider
        label="Encounters"
        name="encounters"
        tooltipHeading="What is an encounter?"
        tooltip={
          <>
            <p>
              For <b>adaptive tests</b> each encounter represents a student that was given the
              question.
            </p>
            <p>
              For <b>static tests</b> each encounter represents a student that answered the question
              (did not omit).
            </p>
          </>
        }
        defaultValue={defaultEncounterThreshold}
        step={1}
        min={1}
        max={maxEncounters}
        symbol=">="
      />
      <RangeSlider
        label="% correct"
        name="percent-correct"
        defaultValue={defaultPercentCorrect}
        step={5}
        min={0}
        max={100}
        symbol=">="
      />
      <RangeSlider
        label="Icon size"
        name="icon-size"
        defaultValue={defaultItemSize}
        step={1}
        min={6}
        max={36}
      />
      <SortSelector label="Sort items" />
    </>
  );
}
